import { createContext, useEffect, useRef, useState } from 'react';

export const TableScrollContext = createContext(false);

type Props = {
  children: React.ReactNode;
  className?: string;
  hint?: string;
};

export function TableScrollContainer({ children, className, hint = 'Faites défiler ↔' }: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth + 1);
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };
    const resize = new ResizeObserver(update);
    const observeContent = () => {
      resize.disconnect();
      resize.observe(el);
      for (const table of Array.from(el.querySelectorAll('table'))) resize.observe(table);
      update();
    };
    observeContent();
    const mutations = new MutationObserver(observeContent);
    mutations.observe(el, { childList: true, subtree: true, characterData: true });
    el.addEventListener('scroll', update, { passive: true });
    return () => {
      el.removeEventListener('scroll', update);
      resize.disconnect();
      mutations.disconnect();
    };
  }, []);

  const scroll = (direction: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  return (
    <div className={`relative min-w-0 max-w-full ${className || ''}`}>
      {hasOverflow && (
        <div className="flex min-w-0 items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-2 text-xs text-gray-600">
          <span className="min-w-0 truncate">{hint}</span>
          <div className="flex shrink-0">
            <button type="button" onClick={() => scroll(-1)} disabled={atStart} aria-label="Défiler vers la gauche" className="h-11 w-11 rounded text-lg hover:bg-gray-200 disabled:opacity-30">←</button>
            <button type="button" onClick={() => scroll(1)} disabled={atEnd} aria-label="Défiler vers la droite" className="h-11 w-11 rounded text-lg hover:bg-gray-200 disabled:opacity-30">→</button>
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        className="dt-table-scroll min-w-0 max-w-full overflow-x-auto"
        role="region"
        tabIndex={hasOverflow ? 0 : undefined}
        aria-label={hasOverflow ? 'Tableau défilable horizontalement' : 'Tableau'}
      >
        <TableScrollContext.Provider value={true}>{children}</TableScrollContext.Provider>
      </div>
    </div>
  );
}
